import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { partnerService } from "../../services/partners";
import { useAuthStore } from "../../store/authStore";
import Button from "../common/Button";
import Modal from "../common/Modal";
import Input from "../common/Input";
import Table from "../common/Table";
import PartnerForm from "./PartnerForm";
import toast from "react-hot-toast";
import { format } from "date-fns";

const PartnerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isRevenueModalOpen, setIsRevenueModalOpen] = useState(false);
  const { user } = useAuthStore();
  const isAdmin = user?.role === "admin";

  const { data: partner, isLoading } = useQuery({
    queryKey: ["partner", id],
    queryFn: () => partnerService.getById(id),
  });

  const { data: billing } = useQuery({
    queryKey: ["partner-billing", id],
    queryFn: () => partnerService.getBilling(id),
  });

  const deletePartner = useMutation({
    mutationFn: () => partnerService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partners"] });
      toast.success("Partner deleted");
      navigate("/partners");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Error deleting partner");
    },
  });

  const handleDelete = () => {
    if (window.confirm("Are you sure you want to delete this partner?")) {
      deletePartner.mutate();
    }
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return format(new Date(date), "dd MMM yyyy");
  };

  if (isLoading) return <div>Loading...</div>;

  const revenueColumns = [
    { header: "Project", accessor: "projectName" },
    { header: "Total Revenue", accessor: (row) => `₹${(row.totalRevenue || 0).toLocaleString("en-IN")}` },
    { header: "Partner Share %", accessor: (row) => `${row.partnerSharePercentage || 0}%` },
    { header: "Partner Amount", accessor: (row) => `₹${(row.partnerAmount || 0).toLocaleString("en-IN")}` },
    { header: "Company Amount", accessor: (row) => `₹${(row.companyAmount || 0).toLocaleString("en-IN")}` },
    { header: "Date", accessor: (row) => formatDate(row.date || row.createdAt) },
  ];

  const statusColor = partner.status === "active"
    ? "bg-green-100 text-green-700"
    : partner.status === "inactive"
    ? "bg-gray-100 text-gray-700"
    : "bg-red-100 text-red-700";

  const partnerTypes = { commission: "Commission", revenue_share: "Revenue Share", fixed: "Fixed", hybrid: "Hybrid" };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">{partner.name}</h1>
        <div className="space-x-2">
          <Button variant="secondary" onClick={() => navigate("/partners")}>Back</Button>
          {isAdmin && <Button onClick={() => setIsEditModalOpen(true)}>Edit</Button>}
          {isAdmin && <Button variant="danger" onClick={handleDelete}>Delete</Button>}
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-6 space-y-3 mb-6">
        <div className="flex items-center gap-2 mb-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColor}`}>{partner.status}</span>
          <span className="text-sm text-gray-500">DLC ID: {partner.dlcId || "-"}</span>
        </div>
        <p><span className="font-medium">Email:</span> {partner.email}</p>
        <p><span className="font-medium">Phone:</span> {partner.phone}</p>
        <p><span className="font-medium">Company:</span> {partner.company || "-"}</p>
        <p><span className="font-medium">Partner Type:</span> {partnerTypes[partner.partnerType] || partner.partnerType}</p>
        <p><span className="font-medium">Commission Rate:</span> {partner.commissionRate ? `${partner.commissionRate}%` : "-"}</p>
        {partner.fixedAmount && <p><span className="font-medium">Fixed Amount:</span> ₹{partner.fixedAmount.toLocaleString("en-IN")}</p>}
        <p><span className="font-medium">Agreement Start:</span> {formatDate(partner.agreementStart)}</p>
        <p><span className="font-medium">Agreement End:</span> {formatDate(partner.agreementEnd)}</p>
        <p><span className="font-medium">Address:</span> {partner.address || "-"}</p>
      </div>

      {billing && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white shadow rounded-lg p-5">
            <p className="text-sm text-gray-500">Total Revenue</p>
            <p className="text-2xl font-bold text-gray-900">₹{(billing.totalRevenue || 0).toLocaleString("en-IN")}</p>
          </div>
          <div className="bg-white shadow rounded-lg p-5">
            <p className="text-sm text-gray-500">Total Partner Earnings</p>
            <p className="text-2xl font-bold text-green-600">₹{(billing.totalPartnerEarnings || 0).toLocaleString("en-IN")}</p>
          </div>
          <div className="bg-white shadow rounded-lg p-5">
            <p className="text-sm text-gray-500">Total Company Earnings</p>
            <p className="text-2xl font-bold text-blue-600">₹{(billing.totalCompanyEarnings || 0).toLocaleString("en-IN")}</p>
          </div>
        </div>
      )}

      <div className="mb-6">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-lg font-semibold">Revenue Entries</h2>
          {isAdmin && <Button onClick={() => setIsRevenueModalOpen(true)}>+ Add Revenue</Button>}
        </div>
        <Table columns={revenueColumns} data={partner.revenueEntries || []} />
      </div>

      {isAdmin && partner.bankDetails && (
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold mb-3">Bank Details</h2>
          <div className="space-y-2">
            <p><span className="font-medium">Account Name:</span> {partner.bankDetails.accountName || "-"}</p>
            <p><span className="font-medium">Account Number:</span> {partner.bankDetails.accountNumber || "-"}</p>
            <p><span className="font-medium">Bank Name:</span> {partner.bankDetails.bankName || "-"}</p>
            <p><span className="font-medium">IFSC Code:</span> {partner.bankDetails.ifscCode || "-"}</p>
          </div>
        </div>
      )}

      {partner.notes && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-3">Notes</h2>
          <p className="text-gray-700 whitespace-pre-wrap">{partner.notes}</p>
        </div>
      )}

      {isAdmin && (
        <>
          <Modal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            title="Edit Partner"
          >
            <PartnerForm
              partner={partner}
              onSuccess={() => {
                setIsEditModalOpen(false);
                queryClient.invalidateQueries({ queryKey: ["partner", id] });
              }}
            />
          </Modal>
          <Modal
            isOpen={isRevenueModalOpen}
            onClose={() => setIsRevenueModalOpen(false)}
            title="Add Revenue Entry"
          >
            <RevenueForm
              partnerId={id}
              onSuccess={() => {
                setIsRevenueModalOpen(false);
                queryClient.invalidateQueries({ queryKey: ["partner", id] });
                queryClient.invalidateQueries({ queryKey: ["partner-billing", id] });
              }}
            />
          </Modal>
        </>
      )}
    </div>
  );
};

const RevenueForm = ({ partnerId, onSuccess }) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();
  const [loading, setLoading] = useState(false);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      await partnerService.addRevenue(partnerId, data);
      toast.success("Revenue entry added");
      reset();
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || "Error adding revenue");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Input
        label="Project Name"
        {...register("projectName", { required: "Project name is required" })}
        error={errors.projectName?.message}
      />
      <Input
        label="Total Revenue"
        type="number"
        {...register("totalRevenue", { required: "Total revenue is required" })}
        error={errors.totalRevenue?.message}
      />
      <Input
        label="Partner Share Percentage"
        type="number"
        {...register("partnerSharePercentage", { required: "Partner share is required", min: 0, max: 100 })}
        error={errors.partnerSharePercentage?.message}
      />
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Notes</label>
        <textarea
          {...register("notes")}
          rows={3}
          className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-lg text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 placeholder:text-gray-400 hover:border-gray-400"
        />
      </div>
      <Button type="submit" disabled={loading}>
        {loading ? "Saving..." : "Add Revenue"}
      </Button>
    </form>
  );
};

export default PartnerDetails;
